import React from 'react';
import { Card, Space, Button, Typography, Tag, Avatar, Row, Col, Divider } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, PhoneOutlined, MoreOutlined, MessageOutlined } from '@ant-design/icons';
import { SmartTableColumn } from '../types';

const { Text } = Typography;

// Utility function to format values based on format type
const formatValue = (
  value: any,
  format?: string,
  decimalPlaces: number = 2
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    return String(value);
  }

  switch (format) {
    case 'currency':
      return `₹${numValue.toFixed(decimalPlaces)}`;
    case 'percentage':
      return `${numValue.toFixed(decimalPlaces)}%`;
    case 'decimal':
    case 'number':
      return numValue.toFixed(decimalPlaces);
    default:
      return String(value);
  }
};

interface MobileCardComponentProps {
  columns: SmartTableColumn[];
  dataSource: any[];
  onEditClick?: (record: any) => void;
  onDeleteClick?: (record: any) => void;
  onViewClick?: (record: any) => void;
  onRowClick?: (record: any) => void;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;
  customActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (record: any) => void;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    tooltip?: string;
    confirmMessage?: string;
  }>;
  rowKey?: string;
  title?: string; // For leads, patients, etc.
}

export const MobileCardComponent: React.FC<MobileCardComponentProps> = ({
  columns,
  dataSource,
  onEditClick,
  onDeleteClick,
  onViewClick,
  onRowClick,
  showEdit = true,
  showDelete = true,
  showView = false,
  customActions = [],
  rowKey = 'id',
  title = 'Items',
}) => {
  // Helper function to render field value
  const renderFieldValue = (column: SmartTableColumn, record: any) => {
    const value = record[column.dataIndex];
    
    // If column has custom render function, use it
    if (column.render) {
      return column.render(value, record);
    }

    // Handle formatting
    if (column.format) {
      return formatValue(value, column.format, column.decimalPlaces);
    }

    // Handle phone numbers
    if (column.dataIndex.toLowerCase().includes('phone') || 
        column.dataIndex.toLowerCase().includes('mobile')) {
      return (
        <Space size="small">
          <PhoneOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
          <Text copyable style={{ fontSize: '12px' }}>{value}</Text>
        </Space>
      );
    }

    // Handle status-like fields with tags
    if (column.dataIndex.toLowerCase().includes('status') && value) {
      const getStatusColor = (status: string) => {
        const lowerStatus = status.toLowerCase();
        if (lowerStatus.includes('new') || lowerStatus.includes('active')) return 'blue';
        if (lowerStatus.includes('completed') || lowerStatus.includes('success') || lowerStatus.includes('converted')) return 'green';
        if (lowerStatus.includes('pending') || lowerStatus.includes('waiting')) return 'orange';
        if (lowerStatus.includes('cancelled') || lowerStatus.includes('failed')) return 'red';
        if (lowerStatus.includes('clinic')) return 'purple';
        return 'default';
      };

      return (
        <Tag color={getStatusColor(value)} style={{ fontSize: '11px', padding: '2px 8px' }}>
          {value}
        </Tag>
      );
    }

    // Handle date fields
    if (column.dataIndex.toLowerCase().includes('date') || 
        column.dataIndex.toLowerCase().includes('time') ||
        column.dataIndex.toLowerCase().includes('follow')) {
      return (
        <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
          {value}
        </Text>
      );
    }

    // Handle user/name fields
    if (column.dataIndex.toLowerCase().includes('user') || 
        column.dataIndex.toLowerCase().includes('name') ||
        column.dataIndex.toLowerCase().includes('patient')) {
      return (
        <Space size="small">
          <Avatar size="small" style={{ backgroundColor: '#1890ff', fontSize: '10px' }}>
            {typeof value === 'string' ? value.charAt(0).toUpperCase() : 'U'}
          </Avatar>
          <Text strong style={{ fontSize: '14px' }}>{value}</Text>
        </Space>
      );
    }

    // Handle engagement mode and source
    if (column.dataIndex.toLowerCase().includes('engagement') || 
        column.dataIndex.toLowerCase().includes('source')) {
      return (
        <Text style={{ fontSize: '12px' }}>{value}</Text>
      );
    }

    // Default rendering
    return <Text style={{ fontSize: '12px' }}>{value || '-'}</Text>;
  };

  // Helper function to render actions
  const renderActions = (record: any) => {
    const actions = [];

    if (showView) {
      actions.push(
        <Button
          key="view"
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onViewClick?.(record);
          }}
          style={{ color: '#1890ff' }}
        />
      );
    }

    if (showEdit) {
      actions.push(
        <Button
          key="edit"
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onEditClick?.(record);
          }}
          style={{ color: '#faad14' }}
        />
      );
    }

    if (showDelete) {
      actions.push(
        <Button
          key="delete"
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick?.(record);
          }}
          style={{ color: '#ff4d4f' }}
        />
      );
    }

    // Add custom actions
    customActions.forEach((action) => {
      actions.push(
        <Button
          key={action.key}
          type="text"
          size="small"
          icon={action.icon}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick(record);
          }}
          style={{ 
            color: action.danger ? '#ff4d4f' : action.type === 'primary' ? '#1890ff' : '#666'
          }}
        />
      );
    });

    return actions;
  };

  // Helper function to get avatar color based on name
  const getAvatarColor = (name: string) => {
    const colors = ['#1890ff', '#52c41a', '#faad14', '#f759ab', '#722ed1', '#13c2c2'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
      {dataSource.map((record, index) => {
        // Get key fields based on title/type
        const nameField = columns.find(col => 
          col.dataIndex.toLowerCase().includes('name') || 
          col.dataIndex.toLowerCase().includes('patient')
        );
        const phoneField = columns.find(col => 
          col.dataIndex.toLowerCase().includes('phone') || 
          col.dataIndex.toLowerCase().includes('mobile') ||
          col.dataIndex.toLowerCase().includes('contact')
        );
        const statusField = columns.find(col => 
          col.dataIndex.toLowerCase().includes('status')
        );
        const engagementField = columns.find(col => 
          col.dataIndex.toLowerCase().includes('engagement')
        );
        const sourceField = columns.find(col => 
          col.dataIndex.toLowerCase().includes('source')
        );
        const followUpField = columns.find(col => 
          col.dataIndex.toLowerCase().includes('follow')
        );
        const userField = columns.find(col => 
          col.dataIndex.toLowerCase().includes('user') || 
          col.dataIndex.toLowerCase().includes('created')
        );

        const name = nameField ? record[nameField.dataIndex] : `Item ${record[rowKey]}`;
        const phone = phoneField ? record[phoneField.dataIndex] : '';
        const status = statusField ? record[statusField.dataIndex] : '';
        const engagement = engagementField ? record[engagementField.dataIndex] : '';
        const source = sourceField ? record[sourceField.dataIndex] : '';
        const followUp = followUpField ? record[followUpField.dataIndex] : '';
        const user = userField ? record[userField.dataIndex] : '';

        return (
          <Card
            key={record[rowKey] || index}
            size="small"
            style={{ 
              marginBottom: '16px',
              marginLeft: '16px',
              marginRight: '16px',
              marginTop: index === 0 ? '16px' : '0',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8',
              cursor: onRowClick ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
              background: '#ffffff',
            }}
            bodyStyle={{ padding: '16px' }}
            onClick={() => onRowClick?.(record)}
          >
            {/* Top Section - Avatar, Name, Status */}
            <Row gutter={[12, 8]} align="middle" style={{ marginBottom: '12px' }}>
              <Col flex="auto">
                <Space size="small">
                  <Avatar 
                    size="default" 
                    style={{ 
                      backgroundColor: getAvatarColor(name),
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#000', lineHeight: '1.2' }}>
                      {name}
                    </div>
                    {phone && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        {phone}
                      </div>
                    )}
                  </div>
                </Space>
              </Col>
              <Col>
                <Space size="small">
                  {status && (
                    <Tag color={status.toLowerCase().includes('new') ? 'blue' : 
                              status.toLowerCase().includes('converted') ? 'green' : 
                              status.toLowerCase().includes('clinic') ? 'purple' : 'default'}
                         style={{ fontSize: '11px', padding: '2px 8px' }}
                    >
                      {status}
                    </Tag>
                  )}
                  <Button
                    type="text"
                    size="small"
                    icon={<MoreOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle more options
                    }}
                    style={{ color: '#666' }}
                  />
                </Space>
              </Col>
            </Row>

            {/* Middle Section - Engagement and Source */}
            {(engagement || source) && (
              <Row gutter={[16, 8]} style={{ marginBottom: '12px' }}>
                {engagement && (
                  <Col span={12}>
                    <div>
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        Engagement Mode
                      </Text>
                      <div style={{ fontSize: '12px', fontWeight: 500, marginTop: '2px' }}>
                        {engagement}
                      </div>
                    </div>
                  </Col>
                )}
                {source && (
                  <Col span={12}>
                    <div>
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        Source
                      </Text>
                      <div style={{ fontSize: '12px', fontWeight: 500, marginTop: '2px' }}>
                        {source}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            )}

            {/* Bottom Section - Follow Up and Actions */}
            <Row justify="space-between" align="middle">
              <Col>
                {followUp && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      Follow Up
                    </Text>
                    <div style={{ fontSize: '12px', fontWeight: 500, marginTop: '2px' }}>
                      {followUp}
                    </div>
                  </div>
                )}
              </Col>
              <Col>
                <Space size="small">
                  {renderActions(record)}
                </Space>
              </Col>
            </Row>
          </Card>
        );
      })}
    </div>
  );
};
